import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { forgeApi } from '@/lib/forgeApi';
import type { 
  Squad, 
  SquadMember, 
  SquadMemberKPI, 
  Problem,
  CustomerMeeting,
  LetterOfIntent 
} from '@/types/forge';

interface ForgeContextType {
  // Squad data
  currentSquad: Squad | null;
  squadMembers: SquadMember[];
  myKPIs: SquadMemberKPI[];
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Squad management
  refreshSquadData: () => Promise<void>;
  
  // KPI functions
  updateKPIProgress: (kpiId: string, currentCount: number) => Promise<void>;
  submitKPIProof: (kpiId: string, proofUrls: string[], notes?: string) => Promise<void>;
  
  // Problem bank functions
  getProblems: () => Promise<Problem[]>;
  selectProblem: (problemId: string) => Promise<void>;
  
  // Meeting functions
  addMeeting: (meeting: Omit<CustomerMeeting, 'id' | 'created_at'>) => Promise<void>;
  
  // LOI functions
  addLOI: (loi: Omit<LetterOfIntent, 'id' | 'created_at'>) => Promise<void>;
}

const ForgeContext = createContext<ForgeContextType | undefined>(undefined);

export const useForge = () => {
  const context = useContext(ForgeContext);
  if (!context) {
    throw new Error('useForge must be used within a ForgeProvider');
  }
  return context;
};

export const ForgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentSquad, setCurrentSquad] = useState<Squad | null>(null);
  const [squadMembers, setSquadMembers] = useState<SquadMember[]>([]);
  const [myKPIs, setMyKPIs] = useState<SquadMemberKPI[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load squad data when user changes
  useEffect(() => {
    if (user) {
      loadSquadData();
    } else {
      setCurrentSquad(null);
      setSquadMembers([]);
      setMyKPIs([]);
    }
  }, [user]);

  const loadSquadData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get user's squad membership
      const { data: memberships, error: membershipError } = await forgeApi.getUserSquad(user.id);
      
      if (membershipError) throw membershipError;
      
      if (memberships && memberships.length > 0) {
        const membership = memberships[0];
        
        // Load squad details
        const { data: squad, error: squadError } = await forgeApi.getSquad(membership.squad_id);
        if (squadError) throw squadError;
        setCurrentSquad(squad);
        
        // Load all squad members
        const { data: members, error: membersError } = await forgeApi.getSquadMembers(membership.squad_id);
        if (membersError) throw membersError;
        setSquadMembers(members || []);
        
        // Load current user's KPIs
        const { data: kpis, error: kpisError } = await forgeApi.getMemberKPIs(membership.id);
        if (kpisError) throw kpisError;
        setMyKPIs(kpis || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load squad data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSquadData = async () => {
    await loadSquadData();
  };

  const updateKPIProgress = async (kpiId: string, currentCount: number) => {
    try {
      const { error } = await forgeApi.updateKPIProgress(kpiId, currentCount);
      if (error) throw error;
      await refreshSquadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update KPI');
    }
  };

  const submitKPIProof = async (kpiId: string, proofUrls: string[], notes?: string) => {
    try {
      const { error } = await forgeApi.submitKPIProof(kpiId, proofUrls, notes);
      if (error) throw error;
      await refreshSquadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit KPI proof');
    }
  };

  const getProblems = async () => {
    try {
      const { data, error } = await forgeApi.getProblems();
      if (error) throw error;
      return data || [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load problems');
      return [];
    }
  };

  const selectProblem = async (problemId: string) => {
    if (!currentSquad) return;
    
    try {
      const { error } = await forgeApi.selectProblem(currentSquad.id, problemId);
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select problem');
    }
  };

  const addMeeting = async (meeting: Omit<CustomerMeeting, 'id' | 'created_at'>) => {
    if (!currentSquad) return;
    
    try {
      const { error } = await forgeApi.addMeeting({
        ...meeting,
        squad_id: currentSquad.id
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add meeting');
    }
  };

  const addLOI = async (loi: Omit<LetterOfIntent, 'id' | 'created_at'>) => {
    if (!currentSquad) return;
    
    try {
      const { error } = await forgeApi.addLOI({
        ...loi,
        squad_id: currentSquad.id
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add LOI');
    }
  };

  return (
    <ForgeContext.Provider value={{
      currentSquad,
      squadMembers,
      myKPIs,
      isLoading,
      error,
      refreshSquadData,
      updateKPIProgress,
      submitKPIProof,
      getProblems,
      selectProblem,
      addMeeting,
      addLOI
    }}>
      {children}
    </ForgeContext.Provider>
  );
};